/**
 * Phase 25 D-04 — @mention encoding scheme. Mentions are stored in the
 * comment markdown body as a markdown link with a custom URL prefix:
 *
 *   @[Display Name](mention:user:123)        ← internal user
 *   @[Display Name](mention:external:45)     ← external user
 *
 * This survives round-tripping through markdown and is grep-friendly for
 * the Phase 26 notification job. Single source of truth for the regex.
 */

export type MentionKind = 'user' | 'external_user';

export interface ParsedMention {
  kind: MentionKind;
  id: number;
}

/** Regex applied to a markdown-link's `href` to detect mentions. */
export const MENTION_HREF_REGEX = /^mention:(user|external):(\d+)$/;

/**
 * Global regex for finding ALL mention tokens in a raw markdown body.
 * Used by Phase 26 notifier; exported here so the encoding lives in one file.
 */
export const MENTION_LINK_REGEX = /\]\(mention:(user|external):(\d+)\)/g;

/** Encode a mention token for insertion into the textarea. */
export function encodeMention(displayName: string, kind: MentionKind, id: number): string {
  const linkKind = kind === 'user' ? 'user' : 'external';
  return `@[${displayName}](mention:${linkKind}:${id})`;
}

/** Parse a single href; returns null when the href is not a mention. */
export function parseMentionHref(href: string | undefined | null): ParsedMention | null {
  if (!href) return null;
  const m = MENTION_HREF_REGEX.exec(href);
  if (!m) return null;
  const linkKind = m[1] as 'user' | 'external';
  const id = Number(m[2]);
  if (!Number.isFinite(id) || id <= 0) return null;
  return { kind: linkKind === 'user' ? 'user' : 'external_user', id };
}
